import React from 'react';

export const chibidashContent = {
  pageMaxWidth: '1200px',

  breadcrumb: {
    text: 'Back to Home',
    url: '/'
  },

  sections: [
    // HERO
    {
      id: 'hero',
      spacing: 'clamp(24px, 4vw, 48px)',
      blocks: [
        // Icon
        {
          type: 'custom',
          render: () => (
            <img
              src="/chibidash-icon.png"
              alt="ChibiDash"
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                marginBottom: '16px'
              }}
            />
          )
        },

        // Category Badge
        {
          type: 'custom',
          render: () => (
            <span style={{
              background: 'linear-gradient(135deg, #ff6b6b 0%, #4b0082 100%)',
              color: 'white',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              display: 'inline-block',
              marginBottom: '16px'
            }}>
              Anime Trivia
            </span>
          )
        },

        // Title
        {
          type: 'title',
          text: 'ChibiDash',
          size: 'h1'
        },

        // Description
        {
          type: 'text',
          content: 'An anime trivia experience. Test your knowledge across different anime worlds.',
          size: 'large'
        },

        { type: 'spacer', size: 'small' },

        // Buttons
        {
          type: 'custom',
          render: () => (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <a
                href="https://chibidash.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #4b0082 100%)',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '0.9rem'
                }}
              >
                Visit ChibiDash
              </a>
              <span style={{
                background: 'rgba(16, 185, 129, 0.1)',
                color: '#10b981',
                padding: '10px 20px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '0.9rem',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }}>
                Live
              </span>
            </div>
          )
        }
      ]
    },

    // LIVE EMBED
    {
      id: 'live-demo',
      blocks: [
        {
          type: 'custom',
          render: () => (
            <div style={{
              position: 'relative',
              width: '100%',
              height: '80vh',
              minHeight: '600px',
              background: '#000',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: '1px solid var(--border-color)'
            }}>
              <iframe
                src="https://chibidash.com"
                title="ChibiDash"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          )
        }
      ]
    },

    // APP SCREENSHOTS
    {
      id: 'screenshots',
      title: 'Inside ChibiDash',
      blocks: [
        {
          type: 'custom',
          render: () => {
            const screens = [
              { src: '/portfolio-screens/chibidash/cover.png', label: 'Games Library' },
              { src: '/portfolio-screens/chibidash/inside.png', label: 'Character Guessing' }
            ];
            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                maxWidth: '900px',
                margin: '0 auto'
              }}>
                {screens.map((screen, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <img
                      src={screen.src}
                      alt={screen.label}
                      style={{
                        width: '100%',
                        borderRadius: '16px',
                        border: '1px solid var(--border-color)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)'
                      }}
                    />
                    <p style={{
                      color: 'var(--text-secondary)',
                      fontSize: '0.75rem',
                      marginTop: '8px'
                    }}>
                      {screen.label}
                    </p>
                  </div>
                ))}
              </div>
            );
          }
        }
      ]
    },

    // BUILT WITH
    {
      id: 'stack',
      title: 'Built With',
      blocks: [
        {
          type: 'custom',
          render: () => {
            const stack = [
              { name: 'React 18', color: '#61DAFB', bg: 'rgba(97, 218, 251, 0.08)' },
              { name: 'Firebase', color: '#FFCA28', bg: 'rgba(255, 202, 40, 0.08)' },
              { name: 'React Router', color: '#CA4245', bg: 'rgba(202, 66, 69, 0.08)' },
              { name: 'Jikan API', color: '#25A0C5', bg: 'rgba(37, 160, 197, 0.08)' },
              { name: 'AniList API', color: '#02A9FF', bg: 'rgba(2, 169, 255, 0.08)' },
              { name: 'EmailJS', color: '#FC0', bg: 'rgba(255, 204, 0, 0.08)' },
              { name: 'React GA4', color: '#E37400', bg: 'rgba(227, 116, 0, 0.08)' },
            ];
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {stack.map((tech) => (
                  <span key={tech.name} style={{
                    background: tech.bg,
                    border: `1px solid ${tech.color}22`,
                    color: tech.color,
                    padding: '10px 20px',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    letterSpacing: '0.01em',
                    backdropFilter: 'blur(8px)'
                  }}>{tech.name}</span>
                ))}
              </div>
            );
          }
        }
      ]
    }
  ],

  callToAction: {
    brand: { bg: "#1F1C1A", fg: "#F2EDE6", btnBg: "#F59E0B", btnFg: "#1F1C1A", logo: "/chibidash-icon.png", logoWidth: 84, logoRadius: 999 },
    title: 'Try ChibiDash',
    text: 'Test your anime knowledge',
    button: {
      text: 'Launch ChibiDash',
      url: 'https://chibidash.com',
      style: 'primary'
    }
  }
};
